import { Injectable } from '@angular/core';

import { AngularFirestore } from '@angular/fire/compat/firestore';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { map } from 'rxjs/operators';
import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030.min.js';

// CRM customer categories (sales stage) shown on the kundekort.
// Value and label are kept identical so the stored value displays cleanly
// everywhere (list, info popup, export) without any mapping.
export const CUSTOMER_CATEGORIES: string[] = ['Lead', 'Prospect', 'Active', 'Inactive'];

// Customer type — mirrors the Person/Company toggle in the Cuboid customer
// profile workflow. Kept separate from CUSTOMER_CATEGORIES (sales stage).
export const CUSTOMER_TYPES: string[] = ['Person', 'Company'];

// Roles a contact person can hold on a customer. "Key holder" is listed
// first as it is the most relevant for building access.
export const CONTACT_ROLES: string[] = ['Key holder', 'Invoice recipient', 'Purchasing', 'Technical', 'Sales', 'Management'];

// Frigg system models shown on the kundekort and selectable per system.
export const SYSTEM_TYPES: string[] = ['Frigg Kompakt', 'Frigg Standard', 'Frigg Modul'];

// How a lead/customer first got in touch — shown on the kundekort Info tab.
export const LEAD_SOURCES: string[] = ['Referral', 'Inbound', 'Outbound', 'Event', 'Website', 'Other'];

// Days before a system's lease end that it counts as "ending soon" (and the reminder fires).
export const LEASE_WARN_DAYS = 30;

export type LeaseState = 'ok' | 'soon' | 'ended';

// leaseEndDate is 'YYYY-MM-DD' on system_info; null when no date is set.
export function leaseStatus(leaseEndDate: string | null | undefined): { state: LeaseState, days: number } | null {
  if (!leaseEndDate || !/^\d{4}-\d{2}-\d{2}$/.test(leaseEndDate)) return null;
  const end = new Date(leaseEndDate + 'T00:00:00');
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((end.getTime() - today.getTime()) / 86400000);
  return { state: days < 0 ? 'ended' : days <= LEASE_WARN_DAYS ? 'soon' : 'ok', days };
}

@Injectable({
  providedIn: 'root'
})
export class KundeKortService {
  constructor(
    private afs: AngularFirestore, private http: HttpClient, private auth: AuthService) {

  }

  // PowerOffice customer for a card (powerOfficeCustomer.js): 'preview' is
  // read-only and reports the server's environment, which every other action
  // must echo back. Super-admin only, proven by the ID token.
  async powerOfficeCustomer(kundekortId: string, action: 'preview' | 'link-parent' | 'create' | 'update', env?: string): Promise<any> {
    return this.powerOfficePost('powerOfficeCustomer', { kundekortId, action, environment: env });
  }

  // Audit log of everything the integration changed (powerOfficeUndo.js list).
  async powerOfficeLog(limit = 300): Promise<any> {
    return this.powerOfficePost('powerOfficeLog', { limit });
  }

  // Reverse one logged action; the server refuses if any part is no longer safe.
  async powerOfficeUndo(actionId: string, env: string): Promise<any> {
    return this.powerOfficePost('powerOfficeUndo', { actionId, environment: env });
  }

  private async powerOfficePost(endpoint: string, body: any): Promise<any> {
    const token = await this.auth.getToken();
    const headers = new HttpHeaders({ 'Content-Type': 'application/json', Authorization: 'Bearer ' + token });
    return this.http.post(environment.apiUrl + '/widgets/' + endpoint, JSON.stringify(body), { headers }).toPromise();
  }

  getCustomers() {
    return this.afs.collection('kunder').snapshotChanges().pipe(map(doc => {
      let newDate = new Date
      return doc.map(obj => {
        return {
          ...obj.payload.doc.data() as object,
          kundekortId: obj.payload.doc.id,
        }
      })
    }))
  }

  // Merge-writes kundekort-owned fields (dates, lease reminder) onto system_info.
  updateSystemFields(systemDocId: string, patch: any) {
    return this.afs.collection('system_info').doc(systemDocId).set(patch, { merge: true });
  }

  getCustomerById(id: string) {
    return this.afs.collection('kunder').doc(id).get();
  }

  // Latest transaction for a system — used to flag "no sales in N days" as
  // a churn/health signal on the kundekort.
  getLastTransactionUnix(systemId: string) {
    return this.afs.collection('transactions', ref => ref.where('systemId', '==', systemId).orderBy('unix', 'desc').limit(1)).get().toPromise();
  }

  // Every physical system (root system_info) — used by the Customer Mapping
  // "Systems" tab to show which systems still need a system-kunder link,
  // independent of any specific month's reconcile.
  getAllSystems$() {
    return this.afs.collection('system_info').snapshotChanges().pipe(map(doc => {
      return doc.map(obj => {
        return {
          ...obj.payload.doc.data() as object,
          systemId: obj.payload.doc.id,
        }
      })
    }))
  }

  // Every system<->customer link, unfiltered (system-kunder is many-to-many).
  getAllSystemKunder$() {
    return this.afs.collection('system-kunder').snapshotChanges().pipe(map(doc => {
      return doc.map(obj => {
        return {
          ...obj.payload.doc.data() as object,
          dbid: obj.payload.doc.id,
        }
      })
    }))
  }

  getKunderById(id) {
    return this.afs.collection('system-kunder', ref=>ref.where('systemId','==',id)).snapshotChanges().pipe(map(doc => {
      return doc.map(obj => {

        return {
          ...obj.payload.doc.data() as object,
          dbid: obj.payload.doc.id,
        }
      })
    }))
  }

  getSystemerByKundekortId(kundekortId) {
    return this.afs.collection('system-kunder', ref=>ref.where('kundekortId','==',kundekortId)).snapshotChanges().pipe(map(doc => {
      return doc.map(obj => {

        return {
          ...obj.payload.doc.data() as object,
          dbid: obj.payload.doc.id,
        }
      })
    }))
  }

  addSystemKunde(systemId, kundekortId) {
    return this.afs.collection('system-kunder').add({
      systemId: systemId,
      kundekortId: kundekortId
    })
  }

  removeSystemKunde(id) {
    return this.afs.collection('system-kunder').doc(id).delete()
  }
  updateCustomer(id, data) {
    let object = {...data}
    object['updatedOn'] = moment().unix();
    return this.afs.collection('kunder').doc(id).update(object);
    
  }

  // A customer here is an INVOICE RECIPIENT, not a legal entity: one organisation
  // can legitimately have several. Oslo Kommune is two separate customers in
  // PowerOffice under one org number (30219 Utdanningsetaten, 30305 Oslo Voksen
  // Opplæring Helsfyr) and must be two kundekort here to invoice them apart;
  // IKEA and Akershus fylkeskommune already have two kundekort each on one org
  // number. A hard block on duplicate org numbers made that impossible to express,
  // so this now REPORTS the clash and lets the caller confirm instead.
  //
  // `powerOfficeCustomerNo` is the identity that actually matters for billing — it
  // decides which ledger an invoice posts to. Two kundekort may share one (IKEA:
  // both post to 10021, kept apart by their own reference on each invoice).
  async findCustomersByOrgId(orgId: string): Promise<{ name: string, kundekortId: string }[]> {
    if (!orgId) { return []; }
    const snap = await this.afs.collection('kunder', ref => ref.where('orgId', '==', orgId)).get().toPromise();
    return snap && !snap.empty ? snap.docs.map(d => ({ name: (d.data() as any).name || 'unnamed', kundekortId: d.id })) : [];
  }

  // Run after a card is saved as Active: give it its PowerOffice customer and
  // Kundenr without a click. A sub-unit takes its parent's Kundenr; any other
  // card gets its customer matched-or-created by org number (powerOfficeCustomer.js).
  // Steps aside — leaving it to the "Create in PowerOffice" button — whenever a
  // person has to choose: no org number, another card with the same org number
  // (Tesla's sites each need their own customer), or a found customer already
  // used by another card. Never throws; returns what to tell the operator.
  async autoPowerOffice(kundekortId: string): Promise<{ type: string, message: string, powerOfficeCustomerNo?: string } | null> {
    const get = async (id: string) => (await this.afs.collection('kunder').doc(id).ref.get()).data() as any;
    const card = await get(kundekortId);
    if (!card || card.category !== 'Active' || card.powerOfficeCustomerNo) return null;
    const skip = (why: string) => ({ type: 'info', message: `PowerOffice: ${why} Use "Create in PowerOffice…" on the card.` });
    try {
      if (card.parentKundekortId) {
        const parent = await get(card.parentKundekortId);
        if (!parent?.powerOfficeCustomerNo) return skip(`${parent?.name || 'The parent card'} has no Kundenr yet, so ${card.name} was not linked.`);
        const preview = await this.powerOfficeCustomer(kundekortId, 'preview');
        const res = await this.powerOfficeCustomer(kundekortId, 'link-parent', preview.environment);
        return { type: 'success', message: `PowerOffice: ${card.name} is invoiced to ${parent.name} (Kundenr ${res.powerOfficeCustomerNo})`, powerOfficeCustomerNo: res.powerOfficeCustomerNo };
      }
      if (!card.orgId) return skip('no org. number, so no customer was created.');
      const sameOrg = (await this.findCustomersByOrgId(card.orgId)).filter(k => k.kundekortId !== kundekortId);
      if (sameOrg.length) return skip(`${sameOrg.map(k => k.name).join(', ')} has the same org. number — choose the right customer by hand.`);
      const preview = await this.powerOfficeCustomer(kundekortId, 'preview');
      if (!preview.customer) return { type: 'error', message: `PowerOffice: ${preview.error || 'no customer could be resolved'}` };
      if (preview.customer.action !== 'create') {
        const no = preview.customer.number;
        const used = await this.afs.collection('kunder', ref => ref.where('powerOfficeCustomerNo', 'in', [String(no), Number(no)])).get().toPromise();
        if (used && !used.empty) return skip(`customer ${no} ${preview.customer.name} is already on ${(used.docs[0].data() as any).name}.`);
      }
      const res = await this.powerOfficeCustomer(kundekortId, 'create', preview.environment);
      const no = res.saved ? String(res.customer.number) : undefined;
      return {
        type: 'success',
        message: `PowerOffice: ${res.customer.action === 'created' ? 'created customer' : 'linked to existing customer'} ${res.customer.number} ${res.customer.name}${res.saved ? '' : ' (demo — not saved)'}`,
        powerOfficeCustomerNo: no,
      };
    } catch (err: any) {
      return { type: 'error', message: `PowerOffice: ${err?.error?.message || err?.message || 'request failed'}` };
    }
  }

  async addCustomer(object, allowDuplicateOrg = false) {
    if (object.orgId && !allowDuplicateOrg) {
      const existing = await this.findCustomersByOrgId(object.orgId);
      if (existing.length) {
        const err: any = new Error(
          `${existing.length} customer(s) already use org number ${object.orgId} ` +
          `(${existing.map(e => e.name).join(', ')}). That is allowed — one organisation can have ` +
          `several invoice recipients — but each needs its own PowerOffice Kundenr.`);
        err.code = 'DUPLICATE_ORG';
        throw err;
      }
    }
    object.updatedOn = moment().unix();
    object.createdOn = moment().unix();
    return this.afs.collection('kunder')
      .add(object)
  }

  deleteCustomer(id) {
   return this.afs.collection('kunder').doc(id)
      .delete()
  }
  deleteContact(id) {
    return this.afs.collection('contacts').doc(id)
      .delete()
  }

  saveAttachments(att){
    return this.afs.collection('attachments').add(att);
  }
  deleteAttachment(id) {
    return this.afs.collection('attachments').doc(id).delete();
  }
  getAttachments(kundekortId){
    return this.afs.collection('attachments', ref => ref.where('kundekortId', '==', kundekortId)).snapshotChanges().pipe(map(doc => {
      return doc.map(obj => {

        return {
          ...obj.payload.doc.data() as object,
          dbid: obj.payload.doc.id,
        }
      })
    }))
  }

  saveNotes(att){
    return this.afs.collection('notater').add(att);
  }
  getNotes(kundekortId){
    return this.afs.collection('notater', ref => ref.where('kundekortId', '==', kundekortId)).snapshotChanges().pipe(map(doc => {
      return doc.map(obj => {

        return {
          ...obj.payload.doc.data() as object,
          dbid: obj.payload.doc.id,
        }
      })
    }))
  }

  // Every contact person across all customers, one read — for the kunder
  // Excel export, which joins them per kundekortId client-side instead of
  // issuing one query per customer.
  getAllContactsOnce(): Promise<any[]> {
    return this.afs.collection('contacts').ref.get()
      .then(s => s.docs.map(d => ({ ...(d.data() as any), dbid: d.id })));
  }

  // ---- Contact book ----
  // One contacts doc per PERSON, linked to any number of kundekort through
  // `kundekortIds`, with the person's roles kept per card in `rolesByKundekort`
  // (invoice recipient at one location, key holder at another). The legacy
  // single `kundekortId` + `roles` pair is kept pointing at the first linked
  // card so older readers keep working.

  // Contacts linked to one card, with `roles` resolved for that card.
  getContacts(kundekortId){
    return this.afs.collection('contacts', ref => ref.where('kundekortIds', 'array-contains', kundekortId)).snapshotChanges().pipe(map(doc => {
      return doc.map(obj => {
        const data = obj.payload.doc.data() as any;
        return {
          ...data,
          dbid: obj.payload.doc.id,
          roles: (data.rolesByKundekort || {})[kundekortId] || [],
        }
      })
    }))
  }

  getAllContacts$() {
    return this.afs.collection('contacts').snapshotChanges().pipe(map(doc => doc.map(obj => ({
      ...obj.payload.doc.data() as any,
      dbid: obj.payload.doc.id,
    }))));
  }

  // New person, optionally linked to a card straight away.
  createContact(person: any, kundekortId?: string, roles: string[] = []) {
    const now = moment().unix();
    const doc: any = { ...person, kundekortIds: [], rolesByKundekort: {}, createdOn: now, updatedOn: now };
    if (kundekortId) {
      doc.kundekortIds = [kundekortId];
      doc.rolesByKundekort = { [kundekortId]: roles };
      doc.kundekortId = kundekortId;
      doc.roles = roles;
    }
    return this.afs.collection('contacts').add(doc);
  }

  // Name/phone/email/position — shared by every card the person is linked to.
  updateContactPerson(id: string, person: any) {
    return this.afs.collection('contacts').doc(id).update({ ...person, updatedOn: moment().unix() });
  }

  setContactRoles(contact: any, kundekortId: string, roles: string[]) {
    const patch: any = { [`rolesByKundekort.${kundekortId}`]: roles, updatedOn: moment().unix() };
    if (contact.kundekortId === kundekortId) { patch.roles = roles; }
    return this.afs.collection('contacts').doc(contact.dbid).update(patch);
  }

  linkContact(contact: any, kundekortId: string, roles: string[] = []) {
    const patch: any = {
      kundekortIds: firebase.firestore.FieldValue.arrayUnion(kundekortId),
      [`rolesByKundekort.${kundekortId}`]: roles,
      updatedOn: moment().unix(),
    };
    if (!contact.kundekortId) { patch.kundekortId = kundekortId; patch.roles = roles; }
    return this.afs.collection('contacts').doc(contact.dbid).update(patch);
  }

  // Removes the person from one card only; they stay in the contact book.
  unlinkContact(contact: any, kundekortId: string) {
    const del = firebase.firestore.FieldValue.delete();
    const patch: any = {
      kundekortIds: firebase.firestore.FieldValue.arrayRemove(kundekortId),
      [`rolesByKundekort.${kundekortId}`]: del,
      updatedOn: moment().unix(),
    };
    if (contact.kundekortId === kundekortId) {
      const next = (contact.kundekortIds || []).find(id => id !== kundekortId);
      patch.kundekortId = next || del;
      patch.roles = next ? ((contact.rolesByKundekort || {})[next] || []) : del;
    }
    return this.afs.collection('contacts').doc(contact.dbid).update(patch);
  }

  // --- Drift (operations): service agreements + meeting minutes ---
  saveDriftAvtale(avtale){
    return this.afs.collection('drift-avtaler').add(avtale);
  }
  getDriftAvtaler(kundekortId){
    return this.afs.collection('drift-avtaler', ref => ref.where('kundekortId', '==', kundekortId)).snapshotChanges().pipe(map(doc => {
      return doc.map(obj => {

        return {
          ...obj.payload.doc.data() as object,
          dbid: obj.payload.doc.id,
        }
      })
    }))
  }
  deleteDriftAvtale(id) {
    return this.afs.collection('drift-avtaler').doc(id).delete();
  }

  saveMoteReferat(referat){
    return this.afs.collection('mote-referat').add(referat);
  }
  getMoteReferat(kundekortId){
    return this.afs.collection('mote-referat', ref => ref.where('kundekortId', '==', kundekortId)).snapshotChanges().pipe(map(doc => {
      return doc.map(obj => {

        return {
          ...obj.payload.doc.data() as object,
          dbid: obj.payload.doc.id,
        }
      })
    }))
  }
  deleteMoteReferat(id) {
    return this.afs.collection('mote-referat').doc(id).delete();
  }

  // update(data){
  //  this.afs.doc(`/company/${data.companyId}/sites/${data.siteId}/systems/${data.systemId}`).get().subscribe(doc=>{
  //    let spData = doc.data();
  //    console.log(spData)
  //    this.afs.collection('machine_mapping').doc(data.dbid).update({systemName:spData['name']})

  //   })
  // }


}
